import React, { CSSProperties, PropsWithChildren, useMemo } from 'react';
import { Label } from '../../typography/Label/Label';
import { Text } from '../../typography/Typography/Typography';
import { Row } from '../../layout/Flex';
import { ClipboardText } from '../../ClipboardText/ClipboardText';

export interface ReadOnlyItemProps extends PropsWithChildren {
  /** Label to display will be suffixed with a colon */
  label: string;
  /* Width of the label in CSS units */
  labelWidth?: string;
  labelStyle?: CSSProperties;
  /** Value as a string or string array (will be joined with a comma) */
  value?: string[] | React.ReactNode;
  /** Gap between label and value, 1rem is the default */
  spaceBetween?: string;
  /** If true, the label will not be bold */
  plainLabel?: boolean;
  /** Alignment of the items along the cross-axis, corresponds to the align-items CSS */
  alignItems?: string;
  /** should the label and content wrap? Defaults to true */
  noWrap?: boolean;
  /**  If true and the value is a string or array of strings, clicking on the value will copy it to the clipboard. Defaults to false. If you want to provide a feedback to the user, attah onCopySuccess and onCopyError handlers */
  copyOnClick?: boolean;
  /** Text to copy to clipboard when a value is clicked, if not provided, the value prop will be used (to be used when value is a complex component) */
  copyText?: string;
  /** Optional callback invoked after text is successfully copied when copyOnClick is true */
  onCopySuccess?: (text: string) => void;
  /** Optional callback invoked when copying fails when copyOnClick is true */
  onCopyError?: (text: string, error: Error) => void;
}

const value2string = (value: string[] | string | number): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return value.toString();
};

/**
 * Displays a label and a value in a row. Value can be either a string or a ReactNode.
 * If value is not provided, children will be used instead. This allows to provide a more complex component
 * as a value.
 * @constructor
 * @param props
 */
export const ReadOnlyItem: React.FC<ReadOnlyItemProps> = ({
  label,
  value,
  spaceBetween = '1rem',
  plainLabel = false,
  children,
  labelWidth,
  alignItems = 'center',
  noWrap = false,
  labelStyle,
  copyOnClick = false,
  copyText,
  onCopySuccess,
  onCopyError,
}) => {
  const labelComponent = useMemo(() => {
    const innerContent = `${label}:`;
    if (plainLabel) {
      return (
        <Text $width={labelWidth} style={labelStyle}>
          {innerContent}
        </Text>
      );
    } else {
      return (
        <Label $width={labelWidth} style={labelStyle}>
          {innerContent}
        </Label>
      );
    }
  }, [label, labelStyle, labelWidth, plainLabel]);

  const valueComponent = useMemo(() => {
    if (value !== undefined) {
      if (typeof value === 'string' || typeof value === 'number' || Array.isArray(value)) {
        const displayValue = value2string(value);
        if (copyOnClick) {
          return (
            <ClipboardText
              text={displayValue}
              textToCopy={copyText}
              onCopy={onCopySuccess}
              onError={onCopyError}
            />
          );
        } else {
          return <Text>{displayValue}</Text>;
        }
      } else {
        return value;
      }
    } else {
      return children;
    }
  }, [children, copyOnClick, copyText, onCopyError, onCopySuccess, value]);

  return (
    <Row
      $gap={spaceBetween}
      $alignItems={alignItems}
      data-type="ReadOnlyItem"
      $flexWrap={noWrap ? 'nowrap' : undefined}
    >
      {labelComponent}
      {valueComponent}
    </Row>
  );
};
